"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import SiteHeader from "@/app/components/SiteHeader";

const NICHES = [
  { slug: "all", label: "Alle" },
  { slug: "trening", label: "Trening" },
  { slug: "kosthold", label: "Kosthold" },
  { slug: "mental-helse", label: "Mental helse" },
  { slug: "karriere", label: "Karriere" },
];

const CAT_LABELS: Record<string, string> = {
  trening: "Trening",
  kosthold: "Kosthold",
  "mental-helse": "Mental helse",
  karriere: "Karriere",
};

const GRADIENTS: Record<string, string> = {
  trening: "from-emerald-200 to-sky-200",
  kosthold: "from-amber-200 to-lime-200",
  "mental-helse": "from-rose-200 to-amber-200",
  karriere: "from-violet-200 to-pink-200",
  default: "from-stone-200 to-stone-300",
};

function dbToCoach(row: any) {
  const name = row.profiles?.full_name || "Coach";
  const initials = name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  const cats: string[] = Array.isArray(row.categories) ? row.categories : [];
  const cat = (cats[0] || "").toLowerCase();
  return {
    slug: row.slug || "",
    name,
    initials,
    title: row.title || "Coach",
    category: cat,
    categoryLabel: CAT_LABELS[cat] || cat || "Coach",
    location: row.location || "Norge",
    bio: row.bio || row.short_intro || "",
    tags: cats,
    gradient: GRADIENTS[cat] || GRADIENTS.default,
    yearsExp: row.years_experience || null,
  };
}

export default function CoachListingPage({ initialCoaches }: { initialCoaches: any[] }) {
  const [activeCat, setActiveCat] = useState<string>("all");
  const coaches = useMemo(() => initialCoaches.map(dbToCoach), [initialCoaches]);
  const filtered = useMemo(() => {
    if (activeCat === "all") return coaches;
    return coaches.filter((c) => c.category === activeCat || c.tags.includes(activeCat));
  }, [coaches, activeCat]);

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen">
      <SiteHeader activePath="/coacher" />

      <section className="px-6 pt-12 pb-8">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3">{coaches.length} verifiserte coacher</p>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-4">Bla, eller la oss matche.</h1>
          <p className="text-stone-600 text-lg max-w-xl font-light mb-10">Vet du hva du vil ha? Bla nedover. Hvis ikke — la oss anbefale tre.</p>

          <div className="flex flex-wrap items-center gap-3">
            {NICHES.map((n) => (
              <button
                key={n.slug}
                onClick={() => setActiveCat(n.slug)}
                className={`px-5 py-2.5 border rounded-full text-sm font-medium transition-all ${
                  activeCat === n.slug
                    ? "bg-stone-900 text-white border-stone-900"
                    : "border-stone-200 hover:border-stone-900 text-stone-700"
                }`}
              >
                {n.label}
              </button>
            ))}
            <span className="ml-auto text-sm text-stone-400 hidden md:inline">Eller</span>
            <Link href="/" className="bg-stone-900 hover:bg-stone-800 text-white px-5 py-2.5 rounded-full text-sm font-medium">
              La oss matche →
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          {filtered.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-3xl p-16 text-center">
              <p className="text-stone-600 mb-2 text-lg font-light">Ingen coacher i denne kategorien ennå.</p>
              <p className="text-stone-400 text-sm mb-6">Vi onboarder coacher fortløpende.</p>
              <Link href="/" className="inline-block bg-stone-900 hover:bg-stone-800 text-white px-6 py-3 rounded-full text-sm font-medium">
                La oss matche deg
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {filtered.map((coach) => (
                <Link
                  key={coach.slug || coach.name}
                  href={`/coacher/${coach.slug}`}
                  className="group bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all border border-stone-200"
                >
                  <div className={`aspect-[4/3] bg-gradient-to-br ${coach.gradient} flex items-end p-8`}>
                    <span className="text-7xl font-light text-stone-700/40">{coach.initials}</span>
                  </div>
                  <div className="p-7">
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="text-xl font-medium tracking-tight">{coach.name}</h3>
                      <span className="text-xs text-stone-400">{coach.location}</span>
                    </div>
                    <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">
                      {coach.categoryLabel}
                      {coach.yearsExp ? ` · ${coach.yearsExp} års erfaring` : ""}
                    </div>
                    {coach.bio && (
                      <p className="text-stone-600 text-sm italic leading-relaxed mb-4 line-clamp-2">&quot;{coach.bio}&quot;</p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">Se profil</span>
                      <span className="text-stone-300 group-hover:text-stone-900 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 px-6 bg-stone-900 text-white text-center">
        <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-4">Vet du fortsatt ikke?</h2>
        <p className="text-stone-400 mb-8 max-w-md mx-auto">
          Skriv det med dine egne ord — vi finner tre forslag til deg på under et minutt.
        </p>
        <Link
          href="/"
          className="inline-block bg-white hover:bg-stone-100 text-stone-900 px-10 py-5 rounded-full font-medium transition-all"
        >
          La oss matche
        </Link>
      </section>

      <footer className="py-12 px-6 text-center text-xs text-stone-400">
        <p>© 2026 Coachguiden</p>
      </footer>
    </div>
  );
}
